import { Transition } from '@headlessui/react';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../assets/icons/index';
import { useScreenSize } from '../../hooks/index';
import { Radio, RadioGroup } from '../radio-group/index';
import { Select, SelectButton, SelectOption, SelectOptions } from '../select/index';

type FilterProps<T extends string> = {
  callback: (value: T) => void;
  translation?: Record<T, string>;
  value?: T;
  variants: T[];
};

export const FilterRadioGroup = <T extends string>({
  callback,
  variants,
  translation,
  value,
}: FilterProps<T>) => {
  const [filter, setFilter] = useState(value || variants[0]);
  const { isMobile } = useScreenSize();

  const handleFilterChange = (newValue: T) => {
    setFilter(newValue);
    callback(newValue);
  };

  if (!isMobile)
    return (
      <RadioGroup
        onChange={handleFilterChange}
        value={filter}
        wrapperClassName="h-fit border-[1px] border-gray-200 rounded-[4px]"
      >
        {variants.map((variant) => (
          <Radio
            className="h-9 bg-white text-sm font-[450] text-[#858593] data-[checked]:bg-brand-25 data-[checked]:font-[550] data-[checked]:text-[#172335]"
            key={variant}
            value={variant}
          >
            <span className="text-nowrap text-label-md">
              {translation ? translation[variant] : variant}
            </span>
          </Radio>
        ))}
      </RadioGroup>
    );

  return (
    <Select onChange={handleFilterChange} value={filter}>
      {({ open }) => (
        <>
          <div className="relative rounded border-gray-200 px-[6px] py-2 lg:hidden">
            <SelectButton>
              <span className="text-sm font-[550] text-[#172335]">
                {translation ? translation[filter] : filter}
              </span>
              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
            </SelectButton>

            <Transition
              as="div"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              show={open}
            >
              <SelectOptions>
                {variants.map((variant) => (
                  <SelectOption key={variant} value={variant}>
                    {({ selected }) => (
                      <span
                        className={twMerge(
                          selected ? 'font-[550]' : 'font-normal',
                          'block text-sm text-gray-700',
                        )}
                      >
                        {translation ? translation[variant] : variant}
                      </span>
                    )}
                  </SelectOption>
                ))}
              </SelectOptions>
            </Transition>
          </div>
        </>
      )}
    </Select>
  );
};
